import {
  ChangeEventHandler,
  CSSProperties,
  Fragment,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Spreadsheet, SpreadsheetAPI } from '../spreadsheet';
// import { ValidationFn } from '../spreadsheet';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  Fade,
  LinearProgress,
  linearProgressClasses,
  LinearProgressProps,
  Modal,
  Stack,
  styled,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import ExcelJS from 'exceljs';
import { listCountrysideService } from '@old/services/domain/base/countryside/countryside-list';
import { useService } from '@old/hooks/use-service';
import { useGlobalStore } from '@old/global-store/use-store';
import { speciesListService } from '@old/services/domain/base/silobag/list/species-list';
import { productListService } from '@old/services/domain/base/silobag/list/product-list';
import { harvestListService } from '@old/services/domain/base/silobag/list/harvest-list';
import { silobagMassiveUploadRecordService } from '@old/services/domain/base/silobag/base/massive-upload-record';
import { listCompanyService } from '@old/services/domain/base/company/company-list';
import { listLotsService } from '@old/services/domain/base/lot/lot-list';

import { humidityOptionListService } from '@old/services/domain/base/silobag/list/humidity-option-list';
import { weightOptionListService } from '@old/services/domain/base/silobag/list/weight-option-list';

import WarningIcon from '@old/assets/svg/warning-red.svg';

import i18n from '../../../libs/language';
import { BetaBadge } from '@old/ui/beta-badge';

type SuccessUploadSilobagData = Awaited<ReturnType<typeof silobagMassiveUploadRecordService>>['summary'];
type SuccessUploadSilobagDataParam = { response: SuccessUploadSilobagData };

type UploadError = {
  error: boolean;
  message: {
    code: number;
    message: string;
  };
};
type UploadFailResponse = { data: UploadError };

type SpreasheetModalProps = ModalProps; //& { loading?: boolean };

export function SilobagMassiveRegisterSpreadsheetModal({ open, onClose }: SpreasheetModalProps) {
  const { organizationId } = useGlobalStore();
  const queryCountrysideList = { organization_id: organizationId, write_permission: true };

  const COLUMN = {
    COMPANY: 0,
    COUNTRYSIDE: 1,
    SILOBAG_NAME: 2,
    SPECIES: 3,
    PRODUCT: 4,
    WEIGHT: 5,
    LOT: 6,
    BAGGING_DATE: 7,
    VARIETY: 8,
    HUMIDITY: 9,
    SIZE: 10,
    HARVEST: 11,
    ORIGIN_LOT_1: 12,
    ORIGIN_LOT_2: 14,
    ORIGIN_LOT_3: 16,
    ORIGIN_LOT_1_WEIGHT: 13,
    ORIGIN_LOT_2_WEIGHT: 15,
    ORIGIN_LOT_3_WEIGHT: 17,
    RESPONSIBLE: 18,
    SILOBAG_BRAND: 19,
    VARIETAL_PURITY: 20,
    SPLIT_GRAIN: 21,
    FILLING_COEFFICIENT: 22,
    HUMIDITY_SOURCE: 23,
    WEIGHT_SOURCE: 24,
    SIDE_CHARGE: 25,
    DIAMETER: 26,
    OBSERVATIONS: 27,
  } as const;

  // [DATA-FETCH]
  const { summary: companies, isLoading: isCompaniesLoading } = useService(listCompanyService, {
    fetchOnMount: true,
  });
  const companiesUniqueOptions = useMemo(
    () =>
      Array.from(
        new Set(companies?.filter((cp) => cp.organizations.id === organizationId).map((cp) => cp.name) ?? [])
      ),
    [companies]
  );

  const { summary: countrysides, isLoading: isCountrysidesLoading } = useService(listCountrysideService, {
    fetchOnMount: [{ query: queryCountrysideList }],
  });
  const countrysidesUniqueOptions = useMemo(
    () => Array.from(new Set(countrysides?.map((cs) => cs.name) ?? [])),
    [countrysides]
  );

  const { summary: lots, isLoading: isLotsLoading } = useService(listLotsService, {
    fetchOnMount: [{ query: { id: organizationId } }],
  });
  const lotsUniqueOptions = useMemo(() => Array.from(new Set(lots?.map((l) => l.name) ?? [])), [lots]);

  const { summary: species, isLoading: isSpeciesLoading } = useService(speciesListService, {
    fetchOnMount: true,
  });
  const { summary: products, isLoading: isProductsLoading } = useService(productListService, {
    fetchOnMount: true,
  });
  const { summary: harvests, isLoading: isHarvestsLoading } = useService(harvestListService, {
    fetchOnMount: true,
  });
  const { summary: humiditySources, isLoading: isHumiditySourcesLoading } = useService(
    humidityOptionListService,
    { fetchOnMount: true }
  );
  const { summary: weightSources, isLoading: isWeightSourcesLoading } = useService(weightOptionListService, {
    fetchOnMount: true,
  });

  const isLoading =
    isCompaniesLoading ||
    isCountrysidesLoading ||
    isLotsLoading ||
    isSpeciesLoading ||
    isProductsLoading ||
    isHarvestsLoading ||
    isHumiditySourcesLoading ||
    isWeightSourcesLoading;

  const submitRowIDX = useRef(1);

  const handleFirstUpload = () => {
    const spreadsheet = spreadsheetRef.current?.state.content;
    if (!spreadsheet) return;

    const firstNonEmptyRowIDX = spreadsheet.findIndex((row) => row.some((c) => c.value[0] !== ''));
    spreadsheetRef.current?.action.structure.row.remove(firstNonEmptyRowIDX, 0);

    // const lastNonEmptyRowIDX = spreadsheet.findLastIndex((row) => row.some((c) => c.value[0] !== ''));
    // spreadsheetRef.current?.action.structure.row.remove(lastNonEmptyRowIDX, spreadsheet.length - 1);

    setTargetTab(0);

    const lastRow = spreadsheet[spreadsheet.length - 1];
    const isEmptyLastRow = lastRow.every((cell) => cell.value[0] === '');
    if (!isEmptyLastRow) {
      // console.log('Empty last row detected... adding new row.');
      spreadsheetRef.current?.action.structure.row.add(1, -1);
    }

    const row = spreadsheet[firstNonEmptyRowIDX];
    if (!row) return;

    handleOnProgressTrackStart(spreadsheet.filter((r) => r.some((c) => c.value[0] !== '')).length);
    handleSubmitData(row);
  };

  const updateResultTabFeedback = () => {
    // FEEDBACK STATES UPDATE
    setCurrentBatchSuccUploadCount((prev) => prev + 1);
    setShowResultTabCount(true);
    setShowResultTabBadge(true);
  };
  const handleUploadSuccess = (summary: unknown) => {
    const { response } = summary as SuccessUploadSilobagDataParam;
    const data = {
      id: response.id,
      name: response.name,
      input_weight: response.input_weight,
      companies: response.companies,
      countrysides: response.countrysides,
      lots: response.lots,
      silobag_species_types: response.silobag_species_types,
      silobag_product_types: response.silobag_product_types,
    };
    setSilobagUploadResultsTrack((prev) => [...prev, { success: true, data }]);
    // console.group('[SUCCESS LOG]');
    // console.log('STEP 1');
    const spreadsheet = spreadsheetRef.current?.state.content;
    if (!spreadsheet) return;

    let emptyRowsOffset = 0;
    for (let i = submitRowIDX.current; i < spreadsheet.length; i++) {
      // console.log(`[${i}] offset: ${emptyRowsOffset + 1}`);
      const row = spreadsheet[i];
      const isEmptyRow = row.every((cell) => cell.value[0] === '');
      if (isEmptyRow) emptyRowsOffset++;
      else break;
    }

    // console.log('STEP 2', spreadsheet.length);
    const isLastRow = submitRowIDX.current + emptyRowsOffset - 1 === spreadsheet.length - 1;
    // console.log('STEP 2', spreadsheet.length, submitRowIDX.current, emptyRowsOffset);
    if (!isLastRow)
      spreadsheetRef.current?.action.structure.row.remove(emptyRowsOffset + 1, submitRowIDX.current - 1);
    if (isLastRow) {
      spreadsheetRef.current?.action.structure.row.remove(emptyRowsOffset, submitRowIDX.current - 1);
      submitRowIDX.current = 1;
      handleOnProgressTrackEnd();
      updateResultTabFeedback();
      return;
    }

    const effectiveSubmitRowIDX = submitRowIDX.current + emptyRowsOffset;

    // console.log('STEP 3', submitRowIDX, effectiveSubmitRowIDX);
    const row = spreadsheet[effectiveSubmitRowIDX];
    if (!row) return;

    // console.log('STEP 4', row[0].value);
    handleOnProgressTrackAdvance(1);
    handleSubmitData(row);

    updateResultTabFeedback();
    // console.groupEnd();
  };

  const handleUploadFail = (error: unknown) => {
    const ERROR_CODES = {
      COMPANY_NOT_FOUND: 20004,
      COUNTRYSIDE_NOT_FOUND: 20005,
      ERROR_SILOBAG_NAME: 20053,
      SPECIES_NOT_FOUND: 20037,
      PRODUCT_NOT_FOUND: 20003,
      EMPTY_WEIGHT: 20010,
      INVALID_INTEGER: 20011,
      LOT_NOT_FOUND: 20006,
      INVALID_DATE_FORMAT: 20001,
      EMPTY_BAGGING_DATE: 20044,
      INVALID_HUMIDITY: 20012,
      HARVEST_NOT_FOUND: 20019,
      ORIGIN_LOT_NOT_FOUND: 20050,
      INVALID_PURITY: 20014,
      INVALID_SPLIT_GRAIN: 20016,
      INVALID_FILLING_COEFFICIENT: 20018,
      NEGATIVE_FILLING_COEFFICIENT: 20027,
      HUMIDITY_SOURCE: 20007,
      WEIGHT_SOURCE: 20009,
      SIDE_CHARGE: 20008,
    };
    const MAP_ERROR_CODE_TO_COL_IDX: Record<number, number> = {
      [ERROR_CODES.COMPANY_NOT_FOUND]: COLUMN.COMPANY,
      [ERROR_CODES.COUNTRYSIDE_NOT_FOUND]: COLUMN.COUNTRYSIDE,
      [ERROR_CODES.ERROR_SILOBAG_NAME]: COLUMN.SILOBAG_NAME,
      [ERROR_CODES.SPECIES_NOT_FOUND]: COLUMN.SPECIES,
      [ERROR_CODES.PRODUCT_NOT_FOUND]: COLUMN.PRODUCT,
      [ERROR_CODES.EMPTY_WEIGHT]: COLUMN.WEIGHT,
      [ERROR_CODES.INVALID_INTEGER]: COLUMN.WEIGHT,
      [ERROR_CODES.LOT_NOT_FOUND]: COLUMN.LOT,
      [ERROR_CODES.INVALID_DATE_FORMAT]: COLUMN.BAGGING_DATE,
      [ERROR_CODES.EMPTY_BAGGING_DATE]: COLUMN.BAGGING_DATE,
      [ERROR_CODES.INVALID_HUMIDITY]: COLUMN.HUMIDITY,
      [ERROR_CODES.HARVEST_NOT_FOUND]: COLUMN.HARVEST,
      [ERROR_CODES.ORIGIN_LOT_NOT_FOUND]: COLUMN.ORIGIN_LOT_1,
      [ERROR_CODES.INVALID_PURITY]: COLUMN.VARIETAL_PURITY,
      [ERROR_CODES.INVALID_SPLIT_GRAIN]: COLUMN.SPLIT_GRAIN,
      [ERROR_CODES.INVALID_FILLING_COEFFICIENT]: COLUMN.FILLING_COEFFICIENT,
      [ERROR_CODES.NEGATIVE_FILLING_COEFFICIENT]: COLUMN.FILLING_COEFFICIENT,
      [ERROR_CODES.HUMIDITY_SOURCE]: COLUMN.HUMIDITY_SOURCE,
      [ERROR_CODES.WEIGHT_SOURCE]: COLUMN.WEIGHT_SOURCE,
      [ERROR_CODES.SIDE_CHARGE]: COLUMN.SIDE_CHARGE,
    } as const;

    const {
      data: {
        message: { code, message },
      },
    } = error as UploadFailResponse;
    const colIDX = MAP_ERROR_CODE_TO_COL_IDX[code];
    const rowIDX = submitRowIDX.current - 1;
    const targetCell = { col: colIDX, row: rowIDX };
    spreadsheetRef.current?.action.feedback.error.register(message, targetCell);

    // console.group('[FAIL LOG]');
    // console.log('STEP 1');
    const spreadsheet = spreadsheetRef.current?.state.content;
    if (!spreadsheet) return;

    let emptyRowsOffset = 0;
    for (let i = submitRowIDX.current; i < spreadsheet.length; i++) {
      // console.log(`[${i}] offset: ${emptyRowsOffset}`);
      const row = spreadsheet[i];
      const isEmptyRow = row.every((cell) => cell.value[0] === '');
      if (isEmptyRow) emptyRowsOffset++;
      else break;
    }

    // console.log('STEP 2', spreadsheet.length);
    const isLastRow = submitRowIDX.current + emptyRowsOffset - 1 === spreadsheet.length - 1;

    if (!isLastRow)
      spreadsheetRef.current?.action.structure.row.remove(emptyRowsOffset, submitRowIDX.current);
    if (isLastRow) {
      spreadsheetRef.current?.action.structure.row.remove(
        Math.max(0, emptyRowsOffset - 1),
        submitRowIDX.current
      );
      submitRowIDX.current = 1;
      handleOnProgressTrackEnd();
      return;
    }

    const effectiveSubmitRowIDX = submitRowIDX.current + emptyRowsOffset;

    // console.log('STEP 3', submitRowIDX, effectiveSubmitRowIDX);
    const row = spreadsheet?.[effectiveSubmitRowIDX];
    if (!row) return;

    // console.log('STEP 4', row[0].value);
    handleOnProgressTrackAdvance(1);
    handleSubmitData(row);
    submitRowIDX.current++;
    // console.groupEnd();
  };

  const { trigger: triggerMassiveUpload } = useService(silobagMassiveUploadRecordService, {
    onSuccess: handleUploadSuccess,
    onError: handleUploadFail,
  });

  // [STATES]
  const [workbook, setWorkbook] = useState<ExcelJS.Workbook | null>(null);
  // const [isMassiveUploadInProgress, setIsMassiveUploadInProgress] = useState(false);
  // const [uploadLogMessage, setUploadLogMessage] = useState('');
  const [progressTrack, setProgressTrack] = useState({
    isInProgress: false,
    totalCount: 0,
    currentCount: 0,
  });
  const handleOnProgressTrackStart = (totalCount: number) =>
    setProgressTrack({ isInProgress: true, totalCount, currentCount: 0 });
  const handleOnProgressTrackEnd = () =>
    setProgressTrack({ isInProgress: false, totalCount: 0, currentCount: 0 });
  const handleOnProgressTrackAdvance = (step: number) =>
    setProgressTrack((prev) => ({ ...prev, currentCount: prev.currentCount + step }));
  const [silobagUploadResultsTrack, setSilobagUploadResultsTrack] = useState<
    { success: boolean; data: SuccessUploadSilobagData }[]
  >([]);

  // [REFS]
  const spreadsheetRef = useRef<SpreadsheetAPI>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleButtonClick = () => fileInputRef.current?.click();

  // [VALIDATIONS]
  const VALIDATION = {
    ONLY_NUMS: (value: string) => /^[0-9]*$/.test(value),
    FLOAT_NUMS: (value: string) => /^-?[0-9]*((?<=\d)\.(?=\d))?[0-9]*$/.test(value) && value !== '-',
    TYPING_FLOAT_NUMS: (value: string) => /^-?[0-9]*\.?[0-9]*$/.test(value),
    BETWEEN_0_AND_100: (value: string) => /^(\d{0,2}|\d{0,2}[.]|\d{0,2}[.]\d{1,2}|100)$/.test(value),
    UNIQUE_OPTION: (value: string[]) => value.length === 1,
  };
  const VALIDATION_FN = {
    SINGLE_OPTION: (value: string[]) => {
      if (value.length === 0) {
        const feedback = {
          type: 'error' as const,
          message: 'Se debe seleccionar alguna opción.',
        };
        return feedback;
      }
      if (value.length > 1) {
        const feedback = {
          type: 'error' as const,
          message: 'Solo se debe seleccionar una única opción.',
        };
        return feedback;
      }
      return null;
    },
    NUMBER_INPUT: (value: string[]) => {
      const isValidNumber = VALIDATION.ONLY_NUMS(value[0]);
      if (!isValidNumber) {
        const feedback = {
          type: 'error' as const,
          message: i18n.t('app.massive_register.positive_number_error'),
        };
        return feedback;
      }
      return null;
    },
    INTEGER_INPUT: (value: string[]) => {
      const feedback = VALIDATION_FN.NUMBER_INPUT(value);
      if (feedback) return feedback;

      const isValidInteger = Number.isInteger(Number(value[0]));
      if (!isValidInteger) {
        const feedback = {
          type: 'error' as const,
          message: i18n.t('app.massive_register.integer_number_error'),
        };
        return feedback;
      }

      return null;
    },
    GREATER_THAN_1000: (value: string[]) => {
      const feedback = VALIDATION_FN.NUMBER_INPUT(value);
      if (feedback) return feedback;

      const isValid = parseInt(value[0]) >= 1000;
      if (!isValid) {
        const feedback = {
          type: 'error' as const,
          message: i18n.t('app.massive_register.greater_than_1000_error'),
        };
        return feedback;
      }

      return null;
    },
    BETWEEN_0_AND_100: (value: string[]) => {
      const isValidNumber = VALIDATION.FLOAT_NUMS(value[0]);
      if (!isValidNumber) {
        const feedback = {
          type: 'error' as const,
          message: i18n.t('app.massive_register.not_a_number_error'),
        };
        return feedback;
      }

      const isValidRange = VALIDATION.BETWEEN_0_AND_100(value[0]);
      if (!isValidRange) {
        const feedback = {
          type: 'warning' as const,
          message: i18n.t('app.massive_register.between_0_to_100_error'),
        };
        return feedback;
      }

      return null;
    },
    POSITIVE: (value: string[]) => {
      const isValidNumber = VALIDATION.FLOAT_NUMS(value[0]);
      if (!isValidNumber) {
        const feedback = {
          type: 'error' as const,
          message: i18n.t('app.massive_register.not_a_number_error'),
        };
        return feedback;
      }

      const isValidRange = parseFloat(value[0]) > 0;
      if (!isValidRange) {
        const feedback = {
          type: 'error' as const,
          message: i18n.t('app.massive_register.greater_than_0_error'),
        };
        return feedback;
      }

      return null;
    },
  };

  // [HANDLERS]
  const handleFileUpload: ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const arrayBuffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);

    setWorkbook(workbook);
  };

  const handleSubmitData = (row: Spreadsheet[0]) => {
    const originLote1 = row[12].value[0];
    // const originLote1Weight = row[13].value[0];
    const originLote2 = row[14].value[0];
    // const originLote2Weight = row[15].value[0];
    const originLote3 = row[16].value[0];
    // const originLote3Weight = row[17].value[0];

    const size = row[10].value[0] !== '' ? row[10].value[0] : null;
    const bagging_date = row[7].value[0] !== '' ? row[7].value[0] : null;
    const varietal_purity = row[20].value[0] !== '' ? row[20].value[0] : null;
    const split_grain_percentage = row[21].value[0] !== '' ? row[21].value[0] : null;
    const fillying_coefficient = row[22].value[0] !== '' ? row[22].value[0] : null;

    const body = {
      // PRINCIPAL INFO
      company_name: row[0].value[0],
      countryside_name: row[1].value[0],
      name: row[2].value[0],
      silobag_species_type_name: row[3].value[0],
      silobag_product_type_name: row[4].value[0],
      input_weight: row[5].value[0],
      lot_name: row[6].value[0],
      bagging_date,
      variety: row[8].value[0],
      initial_humidity: row[9].value[0],
      size,
      silobag_harvest_name: row[11].value[0],
      silobags_origin_lots: [originLote1, originLote2, originLote3].filter(Boolean),

      // ADDITIONAL INFO
      responsible_for_bagging: row[18].value[0],
      silobag_brand: row[19].value[0],
      varietal_purity,
      split_grain_percentage,
      fillying_coefficient,
      humidity_source_name: row[23].value[0],
      weight_source_name: row[24].value[0],
      side_charge_description: row[25].value[0],
      diameter: row[26].value[0],
      observations: row[27].value[0],
    };

    triggerMassiveUpload({ body });
  };

  const header = [
    /* A */ { headerName: i18n.t('app.company') },
    /* B */ { headerName: i18n.t('app.countryside') },
    /* C */ { headerName: i18n.t('app.silobag_name') },
    /* D */ { headerName: i18n.t('app.species') },
    /* E */ { headerName: i18n.t('app.silobag_data.product') },
    /* F */ { headerName: i18n.t('app.silobag_data.weight_bagging') },
    /* G */ { headerName: i18n.t('app.silobag_data.lot_stock') },
    /* H */ { headerName: i18n.t('app.silobag_data.bagging_date2') },
    /* I */ { headerName: i18n.t('app.silobag_data.variety') },
    /* J */ { headerName: i18n.t('app.silobag_data.humidity_bagging') },
    /* K */ { headerName: i18n.t('app.silobag_data.silobag_size') },
    /* L */ { headerName: i18n.t('app.silobag_data.harvest') },
    /* M */ { headerName: i18n.t('app.silobag_data.lot_origin_1') }, // Opcional y desplegable (validar opción correcta) ❌ no sé cómo recuperar los lotes todavía
    /* N */ { headerName: i18n.t('app.silobag_data.weight_lot_origin_1') }, // ✅ Opcional y validación numérica
    /* O */ { headerName: i18n.t('app.silobag_data.lot_origin_2') }, // Opcional y desplegable (validar opción correcta) ❌ no sé cómo recuperar los lotes todavía
    /* P */ { headerName: i18n.t('app.silobag_data.weight_lot_origin_2') }, // ✅ Opcional y validación numérica
    /* Q */ { headerName: i18n.t('app.silobag_data.lot_origin_3') }, // Opcional y desplegable (validar opción correcta) ❌ no sé cómo recuperar los lotes todavía
    /* R */ { headerName: i18n.t('app.silobag_data.weight_lot_origin_3') }, // ✅ Opcional y validación numérica
    /* S */ { headerName: i18n.t('app.silobag_data.bagging_responsible') }, // ✅ Opcional y sin validaciones
    /* T */ { headerName: i18n.t('app.slobag_brand') }, // ✅ Opcional y sin validaciones
    /* U */ { headerName: i18n.t('app.silobag_data.varietal_purity') }, // ✅ Opcional y validación numérica, 2 decimales y entre 0 y 100
    /* V */ { headerName: i18n.t('app.silobag_data.split_grain') }, // ✅ Opcional y validación numérica, 2 decimales y entre 0 y 100
    /* W */ { headerName: i18n.t('app.silobag_data.fillying_coefficient') }, // ✅ Opcional y validación numérica y positivo
    /* X */ { headerName: i18n.t('app.silobag_data.source_humidity') }, // Opcional y desplegable (validar opción correcta) ❌ no sé dónde está o cuál es el endpoint
    /* Y */ { headerName: i18n.t('app.silobag_data.source_weight') }, // Opcional y desplegable (validar opción correcta) ❌ no sé dónde está o cuál es el endpoint
    /* Z */ { headerName: i18n.t('app.silobag_data.side_charge') }, // Opcional y desplegable (validar opción correcta) ❌ no sé dónde está o cuál es el endpoint
    /* AA */ { headerName: i18n.t('app.silobag_data.diameter') }, // ✅ Opcional y sin validaciones
    /* AB */ { headerName: i18n.t('app.observations') }, // ✅ Opcional y sin validaciones
  ];

  /* EXAMPLE FOR TESTING
UserTIC2338	Est187	Silobag Excel Reg 1	ARROZ	CONSUMO	15000																					
  */
  const [targetTab, setTargetTab] = useState(0);
  const handleOnTabChange = (_: React.SyntheticEvent, newValue: number) => setTargetTab(newValue);
  useEffect(() => (!open ? setWorkbook(null) : undefined), [open]);

  const [openWarningModalOnClose, setOpenWarningModalOnClose] = useState(false);
  const [isSiloRealWorkbook, setIsSiloRealWorkbook] = useState(false);

  // RESULT TAB FEEDBACK STATES
  const [showResultTabBadge, setShowResultTabBadge] = useState(false);
  const [showResultTabCount, setShowResultTabCount] = useState(true);
  const [currentBatchSuccUploadCount, setCurrentBatchSuccUploadCount] = useState(0);
  useEffect(() => {
    if (targetTab === 2) setShowResultTabBadge(false);
    if (targetTab !== 2 && !showResultTabBadge) {
      setShowResultTabCount(false);
      setCurrentBatchSuccUploadCount(0);
    }
  }, [targetTab]);

  return (
    <>
      {open ? (
        <MyModal open={open} onClose={onClose}>
          <Stack direction="column" spacing={1} height="100%" maxHeight="100%">
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" flex={1} spacing={1}>
                <Typography variant="h4" fontWeight={600}>
                  {i18n.t('app.massive_register.title')}
                </Typography>
                <Box style={{ position: 'relative' }}>
                  <Box style={{ position: 'absolute', top: 0, left: 0 }}>
                    <BetaBadge />
                  </Box>
                </Box>
              </Stack>
              <Button
                onClick={() => {
                  const found = spreadsheetRef.current?.state.content.find((row) =>
                    row.some((cell) => cell.value[0] !== '')
                  );
                  if (!found) onClose();
                  else setOpenWarningModalOnClose(true);
                }}
              >
                {i18n.t('app.close')}
              </Button>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography>{i18n.t('app.massive_register.description')}</Typography>
              <Stack direction="row" spacing={1} style={{ flex: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={progressTrack.isInProgress}
                  sx={{
                    color: '#FFFE',
                    background: '#22A7F0',
                    '&:hover': { background: '#22A7F0' },
                  }}
                  onClick={() => {
                    handleButtonClick();
                    setIsSiloRealWorkbook(true);
                  }}
                >
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />
                  Excel SiloReal
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={progressTrack.isInProgress}
                  style={{ color: 'white' }}
                  onClick={() => {
                    handleButtonClick();
                    setIsSiloRealWorkbook(false);
                  }}
                >
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />
                  Excel Genérico
                </Button>
              </Stack>
              <Button
                variant="contained"
                size="small"
                disabled={progressTrack.isInProgress}
                style={{ color: 'white' }}
                onClick={handleFirstUpload}
              >
                {progressTrack.isInProgress ? (
                  <CircularProgress size={16} style={{ marginRight: 8 }} />
                ) : (
                  'ENVIAR'
                )}
              </Button>
            </Stack>
            {progressTrack.isInProgress ? (
              <>
                <LinearProgressWithLabel
                  variant="determinate"
                  value={(progressTrack.currentCount / progressTrack.totalCount) * 100}
                />
                <Stack direction="row" justifyContent="center" alignItems="center">
                  <Typography fontSize={12} sx={{ color: '#A7A7A7' }}>
                    <i>{i18n.t('app.massive_register.loading_silobags_information')}</i>
                  </Typography>
                </Stack>
              </>
            ) : null}
            <Divider style={{ margin: 16 }} />
            <Tabs value={targetTab} onChange={handleOnTabChange} style={{ marginTop: -8 }}>
              <Tab label={i18n.t('app.massive_register.spreadsheet')} />
              <Tab label={i18n.t('app.massive_register.instructions')} />
              {silobagUploadResultsTrack.length > 0 ? (
                <Tab
                  label={
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                      <Badge variant="dot" color="success" invisible={!showResultTabBadge}>
                        <Box px={1} display="flex" alignItems="center">
                          <span>Resultados</span>
                        </Box>
                      </Badge>
                      <Typography fontSize={12} color="textSecondary">
                        {silobagUploadResultsTrack.length}
                      </Typography>
                      {showResultTabCount ? (
                        <Typography fontSize={10} fontWeight={600} color="success">
                          (+{currentBatchSuccUploadCount})
                        </Typography>
                      ) : null}
                    </Stack>
                  }
                  disabled={progressTrack.isInProgress}
                />
              ) : null}
            </Tabs>
            <CustomTabPanel value={targetTab} index={0} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {isLoading ? (
                <Stack justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress size={32} />
                </Stack>
              ) : (
                <Spreadsheet
                  ref={spreadsheetRef}
                  workbook={workbook}
                  isSiloRealWorkbook={isSiloRealWorkbook}
                  initialState={{ row: 1, col: header.length }}
                  header={header}
                  fixedCols
                  readonly={{
                    N: true,
                    P: true,
                    R: true,
                  }}
                  options={{
                    // TODO: tanto los countrysides, como los lotes, son todos los disponibles de manera global, no filtran por establecimiento o compañía todavía.
                    A: companiesUniqueOptions,
                    B: countrysidesUniqueOptions,
                    D: species?.map((s) => s.name),
                    E: products?.map((p) => p.name),
                    L: harvests?.map((h) => h.name),
                    G: lotsUniqueOptions,
                    M: lotsUniqueOptions,
                    O: lotsUniqueOptions,
                    Q: lotsUniqueOptions,
                    X: humiditySources?.map((h) => h.description),
                    Y: weightSources?.map((w) => w.description),
                    Z: ['Lote', 'Camino'], // TODO: falta pasar a servicio, de momento queda hardcodeado por tema de tiempos
                    AA: ['5', '6', '8', '9', '10', '12'],
                  }}
                  // optionsDependency={{
                  //   I: {
                  //     dependsOn: 'H',
                  //     options: (value) => {
                  //       const [option] = value;
                  //       if (option === 'Hola') return ['Hola', 'Qtal'];
                  //       if (option === 'Qtal') return ['Qtal', 'Chau'];
                  //       return [];
                  //     }
                  //   }
                  // }}
                  validation={{
                    A: VALIDATION_FN.SINGLE_OPTION,
                    B: VALIDATION_FN.SINGLE_OPTION,
                    D: VALIDATION_FN.SINGLE_OPTION,
                    E: VALIDATION_FN.SINGLE_OPTION,
                    H: (value: string[]) => {
                      const isValidDate = !isNaN(new Date(value[0]).getTime());
                      if (isValidDate) return null;
                      return {
                        type: 'error',
                        message: i18n.t('app.massive_register.format_date_error'),
                      };
                    },
                    L: VALIDATION_FN.SINGLE_OPTION,
                    G: VALIDATION_FN.SINGLE_OPTION,
                    K: VALIDATION_FN.NUMBER_INPUT,
                    M: VALIDATION_FN.SINGLE_OPTION,
                    O: VALIDATION_FN.SINGLE_OPTION,
                    Q: VALIDATION_FN.SINGLE_OPTION,
                    F: (v) => {
                      const numberFeedback = VALIDATION_FN.NUMBER_INPUT(v);
                      if (numberFeedback) return numberFeedback;

                      const isGreaterThan1000Feedback = VALIDATION_FN.GREATER_THAN_1000(v);
                      if (isGreaterThan1000Feedback) return isGreaterThan1000Feedback;

                      return null;
                    },
                    J: VALIDATION_FN.BETWEEN_0_AND_100,
                    U: VALIDATION_FN.BETWEEN_0_AND_100,
                    V: VALIDATION_FN.BETWEEN_0_AND_100,
                    W: VALIDATION_FN.POSITIVE,
                    AA: VALIDATION_FN.SINGLE_OPTION,
                  }}
                />
              )}
            </CustomTabPanel>
            <CustomTabPanel value={targetTab} index={1} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Stack spacing={1} style={{ padding: 8 }}>
                <ColumnInstructionEntry
                  label={i18n.t('app.company')}
                  required
                  description={i18n.t('app.massive_register.company_description')}
                  type="Coincidencia Exacta"
                  typeLabel={i18n.t('app.massive_register.exact_match')}
                  example="Agro S.A."
                  options={companiesUniqueOptions}
                  clarification={i18n.t('app.massive_register.company_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.countryside')}
                  required
                  description={i18n.t('app.massive_register.countryside_description')}
                  type="Coincidencia Exacta"
                  typeLabel={i18n.t('app.massive_register.exact_match')}
                  example="La Tablada"
                  options={countrysidesUniqueOptions}
                  clarification={i18n.t('app.massive_register.countryside_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_name')}
                  required
                  description={i18n.t('app.massive_register.silobag_description')}
                  type="Libre"
                  typeLabel={i18n.t('app.massive_register.free')}
                  example="Bolsa 4 de soja"
                  clarification={i18n.t('app.massive_register.silobag_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.species')}
                  required
                  description={i18n.t('app.massive_register.species_description')}
                  type="Coincidencia Exacta"
                  typeLabel={i18n.t('app.massive_register.exact_match')}
                  example="Soja"
                  options={species?.map((s) => s.name)}
                  clarification={i18n.t('app.massive_register.species_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.product')}
                  required
                  description={i18n.t('app.massive_register.product_description')}
                  type="Coincidencia Exacta"
                  typeLabel={i18n.t('app.massive_register.exact_match')}
                  example="Semilla"
                  options={products?.map((p) => p.name)}
                  // TODO: revisar si realmente esto tiene que ser una coincidencia exacta o si acepta case insensitive y accent insensitive.
                  clarification={i18n.t('app.massive_register.product_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.weight_bagging')}
                  required
                  description={i18n.t('app.massive_register.weight_bagging_description')}
                  type="Entero Positivo"
                  typeLabel={i18n.t('app.massive_register.positive_integer')}
                  example="150000"
                  clarification={i18n.t('app.massive_register.weight_bagging_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.lot_stock')}
                  required
                  description={i18n.t('app.massive_register.lot_stock_description')}
                  type="Libre"
                  typeLabel={i18n.t('app.massive_register.free')}
                  example="Lote 1"
                  options={lotsUniqueOptions}
                  // TODO: revisar que en el formulario de creación de establecimiento hay una validación para los nuevos lotes que no permite agregar caracteres especiales como el @ (arroba), sin embargo en esta hoja de cálculo no hay ningún tipo de validación al respecto y entonces estaría quedando inconsistente.
                  clarification={i18n.t('app.massive_register.lot_stock_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.bagging_date2')}
                  required
                  description={i18n.t('app.massive_register.bagging_date_description')}
                  type="Fecha"
                  typeLabel={i18n.t('app.date')}
                  example="2023-05-14"
                  clarification={i18n.t('app.massive_register.bagging_date_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.variety')}
                  description={i18n.t('app.massive_register.variety_description')}
                  type="Libre"
                  typeLabel={i18n.t('app.massive_register.free')}
                  example="HB4"
                  clarification={i18n.t('app.massive_register.variety_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.humidity_bagging')}
                  description={i18n.t('app.massive_register.humidity_bagging_description')}
                  type="Porcentaje"
                  typeLabel={i18n.t('app.massive_register.percentage')}
                  example="13.5"
                  clarification={i18n.t('app.massive_register.humidity_bagging_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.silobag_size')}
                  description={i18n.t('app.massive_register.silobag_size_description')}
                  type="Entero Positivo"
                  typeLabel={i18n.t('app.massive_register.positive_integer')}
                  example="60"
                  clarification={i18n.t('app.massive_register.silobag_size_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.harvest')}
                  description={i18n.t('app.massive_register.harvest_description')}
                  type="Coincidencia Exacta"
                  typeLabel={i18n.t('app.massive_register.exact_match')}
                  example="24/25"
                  options={harvests?.map((h) => h.name)}
                  clarification={i18n.t('app.massive_register.harvest_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.lot_origin')}
                  description={i18n.t('app.massive_register.origin_lot_description')}
                  type="Coincidencia Exacta"
                  typeLabel={i18n.t('app.massive_register.exact_match')}
                  example="Lote 3"
                  options={lotsUniqueOptions}
                  clarification={i18n.t('app.massive_register.origin_lot_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.weight_lot')}
                  description={i18n.t('app.massive_register.weight_lot_description')}
                  type="Entero Positivo"
                  typeLabel={i18n.t('app.massive_register.positive_integer')}
                  example="15000"
                  clarification={i18n.t('app.massive_register.weight_lot_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.bagging_responsible')}
                  description={i18n.t('app.massive_register.bagging_responsible_description')}
                  type="Libre"
                  typeLabel={i18n.t('app.massive_register.free')}
                  example="Javier García"
                  clarification={i18n.t('app.massive_register.bagging_responsible_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.slobag_brand')}
                  description={i18n.t('app.massive_register.silobag_brand_description')}
                  type="Libre"
                  typeLabel={i18n.t('app.massive_register.free')}
                  example="Ipesa"
                  clarification={i18n.t('app.massive_register.silobag_brand_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.varietal_purity')}
                  description={i18n.t('app.massive_register.varietal_purity_description')}
                  type="Porcentaje"
                  typeLabel={i18n.t('app.massive_register.percentage')}
                  example="1"
                  clarification={i18n.t('app.massive_register.varietal_purity_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.split_grain')}
                  description={i18n.t('app.massive_register.split_grain_description')}
                  type="Porcentaje"
                  typeLabel={i18n.t('app.massive_register.percentage')}
                  example="2"
                  clarification={i18n.t('app.massive_register.split_grain_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.fillying_coefficient')}
                  description={i18n.t('app.massive_register.fillying_coefficient_description')}
                  type="Real Positivo"
                  typeLabel={i18n.t('app.massive_register.real_positive_number')}
                  example="3.5"
                  clarification={i18n.t('app.massive_register.fillying_coefficient_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.source_humidity')}
                  description={i18n.t('app.massive_register.source_humidity_description')}
                  type="Coincidencia Exacta"
                  typeLabel={i18n.t('app.massive_register.exact_match')}
                  example="Medición manual"
                  options={humiditySources?.map((h) => h.description)}
                  clarification={i18n.t('app.massive_register.source_humidity_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.source_weight')}
                  description={i18n.t('app.massive_register.source_weight_description')}
                  type="Coincidencia Exacta"
                  typeLabel={i18n.t('app.massive_register.exact_match')}
                  example="Corvus"
                  options={weightSources?.map((w) => w.description)}
                  clarification="Puede ser la marca o el modelo del instrumento o alguna refer de medición."
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.side_charge')}
                  description={i18n.t('app.massive_register.side_charge_description')}
                  type="Coincidencia Exacta"
                  typeLabel={i18n.t('app.massive_register.exact_match')}
                  example="Camino"
                  options={['Lote', 'Camino']}
                  clarification="Únicamente se pueden seleccionar 2 opciones disponibles que son: «Lote» o «Camino»."
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.silobag_data.diameter')}
                  description={i18n.t('app.massive_register.diameter_description')}
                  type="Coincidencia Exacta"
                  typeLabel={i18n.t('app.massive_register.exact_match')}
                  example="9"
                  options={['5', '6', '8', '9', '10', '12']}
                  clarification={i18n.t('app.massive_register.diameter_clarification')}
                />
                <ColumnInstructionEntry
                  label={i18n.t('app.observations')}
                  description={i18n.t('app.massive_register.observations_description')}
                  type="Libre"
                  typeLabel={i18n.t('app.massive_register.free')}
                  example="Dado de alta masivamente. Ir a completar el alta en campo con la app celular."
                  clarification={i18n.t('app.massive_register.observations_clarification')}
                />
              </Stack>
            </CustomTabPanel>
            <CustomTabPanel value={targetTab} index={2} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Stack justifyContent="center">
                <Divider orientation="horizontal" flexItem style={{ padding: 0, margin: 0 }} />
                {silobagUploadResultsTrack.slice(0, currentBatchSuccUploadCount).map((s) => (
                  <ResultEntry key={s.data.id} data={s} newEntry />
                ))}
                {showResultTabCount ? (
                  <>
                    <Divider
                      orientation="horizontal"
                      flexItem
                      style={{ margin: '16px 128px' }}
                      textAlign="center"
                    >
                      <Typography fontSize={12} color="success.main">
                        <i>{i18n.t('app.massive_register.last_silobags_loaded')}</i>
                      </Typography>
                    </Divider>
                    <Divider orientation="horizontal" flexItem style={{ padding: 0, margin: 0 }} />
                  </>
                ) : null}
                {silobagUploadResultsTrack.slice(currentBatchSuccUploadCount).map((s) => (
                  <ResultEntry key={s.data.id} data={s} />
                ))}
              </Stack>
            </CustomTabPanel>
          </Stack>
        </MyModal>
      ) : null}
      {openWarningModalOnClose ? (
        <MyModal
          onClose={() => setOpenWarningModalOnClose(false)}
          open={openWarningModalOnClose}
          width="max-content"
          height="max-content"
        >
          <Stack direction="column" spacing={4} style={{ padding: 16 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h4" fontWeight={600} style={{ flex: 1 }}>
                {i18n.t('app.massive_register.modal_title')}
              </Typography>
              <img style={{ width: 32, height: 32 }} src={WarningIcon} />
            </Stack>
            <Typography fontSize={14} color="textSecondary">
              <i>{i18n.t('app.massive_register.close_modal_message')}</i>
            </Typography>
            <Stack direction="row" spacing={4} justifyContent="flex-end">
              <Button
                onClick={() => setOpenWarningModalOnClose(false)}
                sx={{
                  color: 'black',
                  '&:hover': {
                    background: '#00000011',
                  },
                }}
              >
                {i18n.t('app.cancel')}
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{
                  color: '#FFFFFF',
                  background: '#FF0000',
                  fontSize: 14,
                  textTransform: 'none',
                  margin: '10px',
                  '&:hover': {
                    background: '#9c1111',
                  },
                }}
                onClick={() => {
                  setOpenWarningModalOnClose(false);
                  onClose();
                }}
              >
                {i18n.t('app.close')}
              </Button>
            </Stack>
          </Stack>
        </MyModal>
      ) : null}
    </>
  );
}

type ResultEntryProps = { data: { success: boolean; data: SuccessUploadSilobagData }; newEntry?: boolean };
function ResultEntry({ data: s, newEntry }: ResultEntryProps) {
  return (
    <Fragment>
      <Stack
        direction="row"
        spacing={4}
        alignItems="center"
        style={{
          padding: '8px 16px',
          backgroundColor: newEntry ? '#6baa0011' : 'white',
        }}
      >
        <Box
          style={{
            backgroundColor: '#6baa0033',
            border: '1px solid #6baa00',
            borderRadius: 4,
            padding: '2px 8px',
          }}
        >
          <Typography fontSize={11} fontWeight={600} color="#6baa00">
            SUCCESS
          </Typography>
        </Box>
        <Button
          sx={{
            color: '#22A7F0',
            textTransform: 'none',
            textDecoration: 'underline',
            textDecorationColor: '#22A7F0',
            textDecorationThickness: '2px',
            '&:hover': {
              background: '#22a7f02a',
              textDecoration: 'underline',
              textDecorationColor: '#22A7F0',
              textDecorationThickness: '2px',
            },
          }}
          onClick={() => window.open(`/silobag/${s.data.id}`, '_blank')}
        >
          {s.data.id}
        </Button>
        <Box style={{ flex: 1, minWidth: 'max-content' }}>
          <Typography fontSize={14} color="textSecondary">
            <i>{s.data.name}</i>
          </Typography>
        </Box>
        <Box>
          <Stack justifyContent="center" alignItems="center" width="max-content">
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                backgroundColor: '#77777733',
                border: '1px solid #777',
                borderRadius: 4,
                padding: '1px 8px',
              }}
            >
              <Typography fontSize={10} fontWeight={600} color="#777">
                {i18n.t('app.silobag_data.weight_bagging')}
              </Typography>
            </Stack>
            <Typography fontSize={12} color="textSecondary">
              {s.data.input_weight} Kg.
            </Typography>
          </Stack>
        </Box>
        <Box>
          <Stack justifyContent="center" alignItems="center" width="max-content">
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                backgroundColor: '#77777733',
                border: '1px solid #777',
                borderRadius: 4,
                padding: '1px 8px',
              }}
            >
              <Typography fontSize={10} fontWeight={600} color="#777">
                {i18n.t('app.silobag_data.species')}
              </Typography>
            </Stack>

            <Typography fontSize={12} color="textSecondary">
              {s.data.silobag_species_types.name}
            </Typography>
          </Stack>
        </Box>
        <Box>
          <Stack justifyContent="center" alignItems="center" width="max-content">
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                backgroundColor: '#77777733',
                border: '1px solid #777',
                borderRadius: 4,
                padding: '1px 8px',
              }}
            >
              <Typography fontSize={10} fontWeight={600} color="#777">
                {i18n.t('app.silobag_data.product')}
              </Typography>
            </Stack>
            <Typography fontSize={12} color="textSecondary">
              {s.data.silobag_product_types.name}
            </Typography>
          </Stack>
        </Box>
        <Box>
          <Stack justifyContent="center" alignItems="center" width="max-content">
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                backgroundColor: '#77777733',
                border: '1px solid #777',
                borderRadius: 4,
                padding: '1px 8px',
              }}
            >
              <Typography fontSize={10} fontWeight={600} color="#777">
                {i18n.t('app.silobag_data.lot_stock')}
              </Typography>
            </Stack>
            <Typography fontSize={12} color="textSecondary">
              {s.data.lots.name}
            </Typography>
          </Stack>
        </Box>
        <Box>
          <Stack justifyContent="center" alignItems="center" width="max-content">
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                backgroundColor: '#77777733',
                border: '1px solid #777',
                borderRadius: 4,
                padding: '1px 8px',
              }}
            >
              <Typography fontSize={10} fontWeight={600} color="#777">
                {i18n.t('app.countryside')}
              </Typography>
            </Stack>
            <Typography fontSize={12} color="textSecondary">
              {s.data.countrysides.name}
            </Typography>
          </Stack>
        </Box>
        <Box>
          <Stack justifyContent="center" alignItems="center" width="max-content">
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                backgroundColor: '#77777733',
                border: '1px solid #777',
                borderRadius: 4,
                padding: '1px 8px',
              }}
            >
              <Typography fontSize={10} fontWeight={600} color="#777">
                {i18n.t('app.company')}
              </Typography>
            </Stack>
            <Typography fontSize={12} color="textSecondary">
              {s.data.companies.name}
            </Typography>
          </Stack>
        </Box>
      </Stack>
      <Divider orientation="horizontal" flexItem style={{ padding: 0, margin: 0 }} />
    </Fragment>
  );
}

type ColumnInstructionEntryProps = {
  label: string;
  typeLabel: string;
  description: string;
  type: 'Libre' | 'Coincidencia Exacta' | 'Porcentaje' | 'Entero Positivo' | 'Real Positivo' | 'Fecha';
  required?: boolean;
  example?: string;
  options?: string[];
  clarification?: string;
};
function ColumnInstructionEntry(props: ColumnInstructionEntryProps) {
  const { label, typeLabel, description, type, required, example, options, clarification } = props;

  const TYPE_STYLES_MAP = {
    Libre: {
      backgroundColor: '#E6E8EB', // Gris muy suave con una pizca de azul
      border: '2px solid #8C9198',
      color: '#8C9198',
    },
    'Coincidencia Exacta': {
      backgroundColor: '#f2d8c2', // Chocolate vibrante con transparencia
      border: '2px solid #6b4b2e',
      color: '#6b4b2e',
    },
    Porcentaje: {
      backgroundColor: '#d6ecff', // Azul con transparencia
      border: '2px solid #1d76c4',
      color: '#1d76c4',
    },
    'Entero Positivo': {
      backgroundColor: '#ceebcf', // Verde con transparencia
      border: '2px solid #29942d',
      color: '#29942d',
    },
    'Real Positivo': {
      backgroundColor: '#f3d9fa', // Púrpura con transparencia
      border: '2px solid #79288f',
      color: '#79288f',
    },
    Fecha: {
      backgroundColor: '#fce1ea', // Rosa con transparencia
      border: '2px solid #f16795',
      color: '#f16795',
    },
  };

  return (
    <Accordion slotProps={{ heading: { component: 'h4' } }} elevation={2} disableGutters>
      <AccordionSummary>
        <Stack direction="row" spacing={1} alignItems="center" width="100%">
          <Box>
            <Typography fontSize={12} color="textSecondary">
              {i18n.t('app.massive_register.column')}
              {':'}
            </Typography>
          </Box>
          <Box style={{ flex: description !== '' ? undefined : 1 }}>
            <Typography fontSize={16} color="primary" fontWeight={600}>
              {label}
            </Typography>
          </Box>
          {description !== '' ? (
            <>
              <Divider orientation="vertical" flexItem />
              <Box style={{ flex: 1 }}>
                <Typography fontSize={12} color="textSecondary">
                  {description}
                </Typography>
              </Box>
            </>
          ) : null}
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography fontSize={12} color="textSecondary">
              {i18n.t('app.type')}
              {':'}
            </Typography>
          </Box>
          <Box
            style={{
              ...TYPE_STYLES_MAP[type],
              borderRadius: 4,
              padding: '2px 8px',
            }}
          >
            <Typography fontSize={11} fontWeight={600} color={TYPE_STYLES_MAP[type].color}>
              {typeLabel}
            </Typography>
          </Box>
          <Box>
            <Typography fontSize={12} color="textSecondary">
              {i18n.t('app.massive_register.required')}
              {':'}
            </Typography>
          </Box>
          <Box
            style={{
              backgroundColor: required ? '#e5393533' : '#ffb30033',
              border: `0px solid ${required ? '#e53935' : '#ffb300'}`,
              borderRadius: 4,
              padding: '2px 8px',
            }}
          >
            <Typography fontSize={11} fontWeight={600} color={required ? '#e53935' : '#ffb300'}>
              {required ? i18n.t('app.mandatory') : i18n.t('app.optional')}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column" spacing={2} justifyContent="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontSize={12} color="textSecondary">
              <i>
                {i18n.t('app.massive_register.example')}
                {':'}
              </i>
            </Typography>
            <Box
              style={{
                backgroundColor: '#22A7F033',
                border: `0px solid #22A7F0`,
                borderRadius: 4,
                padding: '2px 8px',
              }}
            >
              <Typography fontSize={11} fontWeight={600} color="#22A7F0">
                {example}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} rowGap={1} alignItems="center" flexWrap="wrap">
            {options?.length ? (
              <Typography fontSize={12} color="textSecondary">
                <i>
                  {i18n.t('app.massive_register.available_options')}
                  {':'}
                </i>
              </Typography>
            ) : null}
            {options?.map((o, i) => (
              <Fragment key={o}>
                <Box
                  key={i}
                  style={{
                    backgroundColor: '#6baa0033',
                    border: `0px solid #6baa00`,
                    borderRadius: 4,
                    padding: '2px 8px',
                  }}
                >
                  <Typography fontSize={11} fontWeight={600} color="#6baa00">
                    {o}
                  </Typography>
                </Box>
              </Fragment>
            ))}
          </Stack>
        </Stack>
        {clarification !== '' ? (
          <>
            <Divider textAlign="left" style={{ margin: '8px 0' }}>
              <Typography fontSize={12} color="textSecondary">
                {i18n.t('app.massive_register.clarification')}
              </Typography>
            </Divider>
            <Box>
              <Typography fontSize={14} color="textSecondary">
                {clarification}
              </Typography>
            </Box>
          </>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
}

const GradientLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  // Fondo del contenedor
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
  },
  // La barra interna con gradiente horizontal
  [`& .${linearProgressClasses.bar}`]: {
    background: 'linear-gradient(90deg, #7cbf2d 0%, #1a93c6 100%)',
    // background: 'linear-gradient(90deg, #1a93c6 0%, #7cbf2d 100%)',
    // background: 'radial-gradient(circle, #6baa00, #22A7F0)',
    // background: 'radial-gradient(circle, #22A7F0, #6baa00)',
  },
}));

function LinearProgressWithLabel(props: LinearProgressProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <GradientLinearProgress variant="query" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography sx={{ color: 'text.secondary' }}>{`${Math.round(props.value ?? 0)}%`}</Typography>
      </Box>
    </Box>
  );
}

type TabPanelProps = { index: number; value: number; unmountOnExit?: boolean; style?: CSSProperties };
function CustomTabPanel(props: PropsWithChildren<TabPanelProps>) {
  const { children, value, index, unmountOnExit, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {unmountOnExit ? (
        <>{value === index && <Box sx={{ height: '100%', p: 3 }}>{children}</Box>}</>
      ) : (
        <Box sx={{ height: '100%', p: 3 }}>{children}</Box>
      )}
    </div>
  );
}

type ModalProps = {
  open: boolean;
  onClose: () => void;
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
};
function MyModal({ open, onClose, width, height, children }: PropsWithChildren<ModalProps>) {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: width ?? '90%',
    height: height ?? '90%',
    maxWidth: 1800,
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflowY: 'auto',
  };

  const handleClose = (_: never, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal open={open} keepMounted onClose={handleClose}>
      <Fade in={open} appear={false} timeout={175}>
        <Box sx={style}>{children}</Box>
      </Fade>
    </Modal>
  );
}
